import { useAuiState } from '@assistant-ui/react';
import { MAX_IMAGES_PER_REQUEST, MAX_REQUEST_BYTES } from '@iq/contracts/v1/upload';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useStickyAttachmentsStore } from '../../lib/sticky-attachments-store';

interface ComposerAttachmentSlice {
  file?: { size?: number } | undefined;
  contentType?: string | undefined;
}

/**
 * Running per-request budget readout beneath the composer attachments
 * row. Combines pending composer files + thread-sticky uploads so the
 * user sees their actual exposure relative to the 50 MB / 100-image
 * cap. Hidden when both sources are empty.
 *
 * The tone thresholds (`>75%` amber, `>90%` destructive) line up with
 * the user-facing copy in the adapter's `sizeOverflow` toast — the
 * indicator turns warning-coloured BEFORE the next file would actually
 * tip the request over.
 */
export const AttachmentCapStatus: FC = () => {
  const { t } = useTranslation();
  // `AssistantState` is too wide to cast through, so we erase the selector
  // input type and cast the OUTPUT to the narrow slice the component reads.
  const composerAttachments = useAuiState(
    (s) => (s as { composer: { attachments: readonly unknown[] } }).composer.attachments,
  ) as ReadonlyArray<ComposerAttachmentSlice>;
  const stickyBytes = useStickyAttachmentsStore((s) => s.bytes);
  const stickyImages = useStickyAttachmentsStore((s) => s.imageCount);

  const composerBytes = composerAttachments.reduce((sum, a) => sum + (a.file?.size ?? 0), 0);
  const composerImages = composerAttachments.filter(
    (a) => typeof a.contentType === 'string' && a.contentType.startsWith('image/'),
  ).length;

  const totalBytes = composerBytes + stickyBytes;
  const totalImages = composerImages + stickyImages;

  if (composerAttachments.length === 0 && stickyBytes === 0 && stickyImages === 0) {
    return null;
  }

  const ratio = totalBytes / MAX_REQUEST_BYTES;
  const tone =
    ratio > 0.9 ? 'text-destructive' : ratio > 0.75 ? 'text-amber-600' : 'text-muted-foreground';

  return (
    <div data-testid="attachment-cap-status" className={`px-4 pb-1 text-xs ${tone}`}>
      {t('chat.attachment.status.cap', {
        usedMB: (totalBytes / 1_000_000).toFixed(1),
        maxMB: (MAX_REQUEST_BYTES / 1_000_000).toFixed(0),
        images: totalImages,
        maxImages: MAX_IMAGES_PER_REQUEST,
      })}
    </div>
  );
};
